import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { Select, Tooltip } from '@mantine/core';
import { useEventListener } from 'ahooks';
import { Icon } from '@shakkerai/components';
import { NumberInputGroup } from '@shakkerai/pro-components';
import { translate } from '@shakkerai/util-i18n';
import { upscalerOptions, defaultHiresOptions } from '@shakkerai/pro-constants';
import {
  BASETYPE_VALUE_TO_LABEL,
  MODEL_ILLUS,
  MODEL_SD15,
  MODEL_SDXL,
  SDXL_MODELS_FAMILY,
} from '@shakkerai/pro-models';
import { fixHiresOptions, updateHiresOptions } from '../../helpers';
import { useTempState, setTempState } from '@starflow/app/store';
import { SizeInputGroup } from './size';

const widthFreeSelectComboxProps = {
  position: 'bottom-end',
  width: '!auto',
};

const selectClassNames = {
  dropdown: 'dark-select-dropdown',
  input: 'text-right !pr-6',
};

const hiresSupportedBaseTypes = [MODEL_SDXL, MODEL_SD15, MODEL_ILLUS];

export const HiResEntry = ({ generator }) => {
  const { hiresOptions = defaultHiresOptions, baseType } = generator.data;
  const hiresUnsupported = !hiresSupportedBaseTypes.includes(baseType);

  const openedSubWindow = useTempState('exclusiveWindow');
  const hiresOptionsOpened = openedSubWindow === 'hires-options';

  const handleToggleHiRes = () => {
    const enabled = !hiresOptions.enabled;
    updateHiresOptions(generator.id, { enabled });
    fixHiresOptions(generator.id, 'toggle');

    if (enabled) {
      setTimeout(() => {
        setTempState({ exclusiveWindow: 'hires-options' });
      }, 1);
    }
  };

  useEffect(() => {
    // baseType发生变化的时候也要尝试修复hiresOptions
    fixHiresOptions(generator.id, 'baseType');
  }, [generator.id, generator.data.baseType]);

  const canShowOptions = useMemo(() => {
    return !hiresUnsupported && hiresOptions.enabled;
  }, [hiresUnsupported, hiresOptions]);

  return (
    <Tooltip
      disabled={!hiresUnsupported}
      withArrow
      className="translate-y-[5px] scale-90"
      label={translate('feature_disabled_for_sd3_2', { type: BASETYPE_VALUE_TO_LABEL[baseType] })}
    >
      <div className="flex items-center justify-between mt-3">
        <button
          disabled={hiresUnsupported}
          id={`hires-options-entry-${generator.id}`}
          onClick={handleToggleHiRes}
          className="flex items-center disabled:cursor-not-allowed disabled:opacity-50"
        >
          {canShowOptions ? (
            <div className="flex items-center justify-center w-3.5 h-3.5 bg-[#2D2D2D] rounded-[2px]">
              <Icon className="icon-border_check" fontSize={10} color="#fff" />
            </div>
          ) : (
            <div className="w-3.5 h-3.5 bg-white border border-black/20 rounded-[2px]" />
          )}
          <span className="ml-1 leading-[12px] translate-y-px text-xs text-[#2D2D2D]">
            {translate('setting_hires')}
          </span>
        </button>
        <button
          disabled={hiresUnsupported}
          data-trigger-window="hires-options"
          onClick={(e) => {
            if (canShowOptions) {
              setTempState({ exclusiveWindow: 'hires-options' });
            }
          }}
          className="flex items-center justify-center w-[18px] h-[18px] rounded-sm hover:bg-[#f2f4f6] text-[#7A7A7A] disabled:opacity-30"
        >
          <Icon className={`shk-kuangjia-gengduoshezhi`} fontSize={14} color="#666" />
        </button>
        {hiresOptionsOpened && <HiResOptions generator={generator} />}
      </div>
    </Tooltip>
  );
};

export const HiResOptions = ({ generator }) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(null);
  const hiresOptions = generator.data.hiresOptions || defaultHiresOptions;
  const isSDXL = SDXL_MODELS_FAMILY[generator.data.baseType];

  const handleCloseWindow = useCallback(() => {
    setTempState({ exclusiveWindow: null });
  }, []);

  const sourceSizeValue = useMemo(() => {
    if (hiresOptions.width && hiresOptions.height) {
      return {
        width: hiresOptions.width,
        height: hiresOptions.height,
      };
    }

    if (hiresOptions.scale) {
      return {
        width: Math.round(generator.data.width * hiresOptions.scale),
        height: Math.round(generator.data.height * hiresOptions.scale),
      };
    }

    return {
      width: generator.data.width,
      height: generator.data.height,
    };
  }, [generator.data.width, generator.data.height, hiresOptions]);

  const generateSize = hiresOptions.enabled
    ? generator.data
    : {
        width: Math.round(sourceSizeValue.width * hiresOptions.scale),
        height: Math.round(sourceSizeValue.height * hiresOptions.scale),
      };

  const handleSourceSizeChange = useCallback(
    (value) => {
      updateHiresOptions(generator.id, value);
      fixHiresOptions(generator.id, 'sourceSize');
    },
    [generator.id],
  );

  const maxScale = 2048 / Math.max(sourceSizeValue.width, sourceSizeValue.height);

  const handleScaleChange = useCallback(
    (scale) => {
      updateHiresOptions(generator.id, { scale });
      fixHiresOptions(generator.id, 'scale');
    },
    [generator.id],
  );

  const handleChange = useCallback(
    (value, name) => {
      updateHiresOptions(generator.id, { [name]: value });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generator.id],
  );

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }

    if (event.target?.closest('[data-trigger-window="hires-options"]')) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    setTempState({ exclusiveWindow: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    const ref = document.getElementById(`hires-options-entry-${generator.id}`);
    const { x, y } = ref.getBoundingClientRect();

    setPosition({ x: x - 320, y });
  }, [generator.id]);

  if (!position) {
    return null;
  }

  return createPortal(
    <Rnd default={position} scale={1} className="z-[100]" dragHandleClassName="sub-drag-handle">
      <div ref={containerRef} className="w-[300px] bg-white shadow-light rounded-xs">
        <div className="sub-drag-handle flex items-center justify-between px-4 py-2 cursor-move border-b border-[#efefef] ">
          <span className="text-mini font-semibold text-caption-500">{translate('setting_hires')}</span>
          <button onClick={handleCloseWindow}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#222]" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between h-6">
            <span className="text-xs text-[#7f7f7f]">{translate('hires_original')}</span>
            <SizeInputGroup maxSize={isSDXL ? 1536 : 1024} value={sourceSizeValue} onChange={handleSourceSizeChange} />
          </div>
          <div className="flex items-center justify-between h-6">
            <span className="text-xs text-[#7f7f7f]">{translate('imagetool_upscale_scale')}</span>
            <div className="flex w-40 items-center h-6">
              <NumberInputGroup
                min={1}
                max={maxScale}
                step={0.05}
                allowDecimal={true}
                name="scale"
                inputWidth={45}
                value={hiresOptions.scale}
                onChange={handleScaleChange}
              />
            </div>
          </div>
          <div className="flex items-center justify-between h-6">
            <span className="text-xs text-[#7f7f7f]">{translate('upscale_method')}</span>
            <div className="flex w-40 items-center h-6">
              <Select
                size="xxxs"
                name="upscaler"
                data={upscalerOptions}
                value={hiresOptions.upscaler}
                classNames={selectClassNames}
                className="cn-select flex-1 bordered"
                onChange={(upscaler) => handleChange(upscaler, 'upscaler')}
                comboboxProps={widthFreeSelectComboxProps}
              />
            </div>
          </div>
          <div className="flex items-center justify-between h-6">
            <span className="text-xs text-[#7f7f7f]">{translate('common_denoising')}</span>
            <div className="flex w-40 items-center h-6">
              <NumberInputGroup
                min={0}
                max={1}
                step={0.1}
                allowDecimal={true}
                name="strength"
                inputWidth={45}
                value={hiresOptions.strength}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="flex items-center justify-between h-6">
            <span className="text-xs text-[#7f7f7f]">{translate('hires_step')}</span>
            <div className="flex w-40 items-center h-6">
              <NumberInputGroup
                min={1}
                max={60}
                step={1}
                allowDecimal={false}
                name="steps"
                inputWidth={45}
                value={hiresOptions.steps}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <footer className="flex items-center justify-between p-4 bg-[#FAFAFA]">
          <span className="text-xs text-[#7f7f7f]">{translate('hires_finalimage')}</span>
          <div className="flex items-center text-xs font-semibold">
            <span className="text-[#8c8c8c]">W</span>
            <span className="w-9 text-right text-[#2d2d2d]">{generateSize.width?.toFixed(0)}</span>
            <span className="ml-4 text-[#8c8c8c]">H</span>
            <span className="w-9 text-right text-[#2d2d2d]">{generateSize.height?.toFixed(0)}</span>
          </div>
        </footer>
      </div>
    </Rnd>,
    document.body,
  );
};
